import { X } from 'lucide-react';

import type { GridView, Tone } from '@/features/Visualizer/types';
import { TONES } from '@/features/Visualizer/theme';

interface Props {
  value: GridView;
  onChange: (next: GridView) => void;
}

const TONE_CYCLE: Tone[] = ['accent', 'info', 'success', 'warn', 'muted'];

function nextTone(current: Tone | undefined): Tone | undefined {
  if (!current) return TONE_CYCLE[0];
  const i = TONE_CYCLE.indexOf(current);
  return i === -1 || i === TONE_CYCLE.length - 1 ? undefined : TONE_CYCLE[i + 1];
}

function coerceValue(raw: string): number | string {
  if (raw.trim() !== '' && /^-?\d+(\.\d+)?$/.test(raw.trim())) return Number(raw);
  return raw;
}

/** Trainer-facing replacement for the raw-JSON `grid` view editor — click cells to mark or set the cursor, edit values inline, grow/shrink rows and columns with buttons. */
export function VisualizationGridCanvas({ value, onChange }: Props) {
  const rows = value.values ?? [];
  const marks = value.marks ?? [];
  const cursor = value.cursor ?? null;

  const markAt = (row: number, col: number) => marks.find((m) => m.row === row && m.col === col);

  const setCell = (row: number, col: number, raw: string) => {
    const nextRows = rows.map((r, ri) => (ri === row ? r.map((c, ci) => (ci === col ? coerceValue(raw) : c)) : r));
    onChange({ ...value, values: nextRows });
  };

  const cycleMark = (row: number, col: number) => {
    const current = markAt(row, col);
    const tone = nextTone(current?.tone);
    const nextMarks = marks.filter((m) => !(m.row === row && m.col === col));
    if (tone) nextMarks.push({ row, col, tone, label: current?.label });
    onChange({ ...value, marks: nextMarks });
  };

  const toggleCursor = (row: number, col: number) => {
    const isCurrent = cursor && cursor.row === row && cursor.col === col;
    onChange({ ...value, cursor: isCurrent ? null : { row, col } });
  };

  const numCols = rows[0]?.length ?? 0;

  const addRow = () => onChange({ ...value, values: [...rows, new Array(numCols).fill(0)] });
  const removeRow = (row: number) => onChange({ ...value, values: rows.filter((_, i) => i !== row) });
  const addCol = () => onChange({ ...value, values: rows.map((r) => [...r, 0]) });
  const removeCol = (col: number) =>
    onChange({ ...value, values: rows.map((r) => r.filter((_, i) => i !== col)) });

  return (
    <div className="rounded-md border border-[#ececec] p-3">
      <p className="mb-2 text-[11px] text-[#9ca3af]">
        Click a cell's colour dot to mark it · click a cell's index to set the cursor there
      </p>
      <div className="inline-block">
        {rows.map((row, ri) => (
          <div key={ri} className="flex items-center gap-1">
            {row.map((v, ci) => {
              const mark = markAt(ri, ci);
              const isCursor = !!cursor && cursor.row === ri && cursor.col === ci;
              return (
                <div key={ci} className="relative m-0.5 flex h-12 w-12 flex-col items-center">
                  <div
                    className={`relative flex h-10 w-12 items-center justify-center rounded-md border-2 bg-white ${
                      isCursor ? 'border-primary' : 'border-[#d1d5db]'
                    }`}
                    style={mark ? { backgroundColor: `${TONES[mark.tone]}22`, borderColor: TONES[mark.tone] } : undefined}
                  >
                    <button
                      type="button"
                      aria-label={`Cycle mark colour at row ${ri}, col ${ci}`}
                      onClick={() => cycleMark(ri, ci)}
                      className="absolute -right-1 -top-1 h-3 w-3 rounded-full border border-white"
                      style={{ backgroundColor: mark ? TONES[mark.tone] : '#d1d5db' }}
                    />
                    <input
                      value={String(v)}
                      onChange={(e) => setCell(ri, ci, e.target.value)}
                      className="w-full bg-transparent text-center text-sm outline-none"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleCursor(ri, ci)}
                    className={`text-[9px] ${isCursor ? 'font-semibold text-primary' : 'text-[#9ca3af]'}`}
                  >
                    {ri},{ci}
                  </button>
                </div>
              );
            })}
            <button
              type="button"
              aria-label={`Remove row ${ri}`}
              onClick={() => removeRow(ri)}
              className="ml-1 text-destructive"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        <div className="mt-2 flex items-center gap-2">
          <button
            type="button"
            onClick={addRow}
            className="rounded-md border border-dashed border-[#d1d5db] px-2 py-1 text-xs text-primary hover:border-primary"
          >
            + Row
          </button>
          <button
            type="button"
            onClick={addCol}
            className="rounded-md border border-dashed border-[#d1d5db] px-2 py-1 text-xs text-primary hover:border-primary"
          >
            + Column
          </button>
          {numCols > 0 && (
            <button
              type="button"
              onClick={() => removeCol(numCols - 1)}
              className="text-xs text-destructive hover:underline"
            >
              Remove last column
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
